import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";

interface ActionConfirmationDialogProps {
  open: boolean;
  title: string;
  actionLabel: string;
  actionColor?: "inherit" | "primary" | "secondary" | "success" | "error" | "info" | "warning";
  summary: {
    label: string;
    value: string | number;
  }[];
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
  children?: React.ReactNode;
}

const ActionConfirmationDialog: React.FC<ActionConfirmationDialogProps> = ({
  open,
  title,
  actionLabel,
  actionColor = "primary",
  summary,
  onConfirm,
  onCancel,
  loading = false,
  children,
}) => {
  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Please review the summary below before proceeding with this action.
        </Typography>
        <Box sx={{ bgcolor: "rgba(0,0,0,0.02)", borderRadius: 1, p: 1, mb: children ? 2 : 0 }}>
          <List dense>
            {summary.map((item, index) => (
              <React.Fragment key={index}>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={item.label}
                    secondary={item.value}
                    primaryTypographyProps={{ variant: "caption", fontWeight: "bold", color: "textSecondary" }}
                    secondaryTypographyProps={{ variant: "body2", color: "textPrimary" }}
                  />
                </ListItem>
                {index < summary.length - 1 && <Divider component="li" sx={{ opacity: 0.5 }} />}
              </React.Fragment>
            ))}
          </List>
        </Box>
        {children}
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={actionColor}
          disabled={loading}
          autoFocus
        >
          {loading ? "Processing..." : actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActionConfirmationDialog;
